/* eslint-disable react/no-array-index-key */
import { useState, useEffect } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { Bug } from 'lucide-react';
import { type GenModelPreset } from '@musedrops/shared';
import { getModelPreset } from '../../data/model-preset';
import { CopyableId } from './CopyableId';

interface EntityMetadataProps {
  id: string;
  presets: Array<{
    label: string;
    presetKey: string | null | undefined;
  }>;
  children?: React.ReactNode;
}

/**
 * EntityMetadata component displays entity ID and preset information
 * - Shows truncated ID (first 5 chars + "...")
 * - Click to copy full ID to clipboard
 * - Displays preset keys for non-null/empty values
 * - Shows bug icon for debug presets
 * - All items on same line with consistent spacing
 */
export const EntityMetadata = ({ id, presets, children }: EntityMetadataProps) => {
  const [presetDetails, setPresetDetails] = useState<
    Record<string, GenModelPreset | null>
  >({});

  // Fetch preset details to check debug status
  useEffect(() => {
    const fetchPresetDetails = async () => {
      const validKeys = presets
        .filter(p => p.presetKey && p.presetKey.trim() !== '')
        .map(p => p.presetKey as string);

      const details: Record<string, GenModelPreset | null> = {};

      await Promise.all(
        validKeys.map(async key => {
          try {
            const preset = await getModelPreset(key);
            details[key] = preset;
          } catch {
            details[key] = null;
          }
        })
      );

      setPresetDetails(details);
    };

    fetchPresetDetails();
  }, [presets]);

  // Filter out null/undefined/empty presets
  const validPresets = presets.filter(
    p => p.presetKey && p.presetKey.trim() !== ''
  );

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
      <CopyableId id={id} variant="caption" />

      {children}

      {validPresets.map((preset, index) => {
        const presetInfo = presetDetails[preset.presetKey as string];
        const isDebug = presetInfo?.isDebug;

        return (
          <Box
            key={index}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary">
              {preset.label}: {preset.presetKey}
            </Typography>
            {isDebug && (
              <Tooltip title="Debug preset was used">
                <Bug size={12} color="#f44336" style={{ flexShrink: 0 }} />
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
